/* SpawnAnything — browser client for the AI recipe server (POST /api/generate via the Vite proxy).
   Never throws: on network failure, timeout or a bad response it returns a local fallback recipe. */
import { validateUnitRecipe, fallbackUnitRecipe } from '../game/recipeSchema.js';
import { isBlocked } from '../game/blocklist.js';

/**
 * @param {'unit'|'building'} kind
 * @param {string} prompt
 * @param {{ era?: string, timeoutMs?: number, endpoint?: string, signal?: AbortSignal,
 *           validateBuilding?: (r: any) => { ok: boolean, recipe: any }, fallbackBuilding?: (prompt: string) => any }} [opts]
 *   validateBuilding / fallbackBuilding: pass validateBuildingRecipe / a fallback from src/game/buildings.js
 *   (kept injectable so this module does not depend on buildings.js).
 * @returns {Promise<{ recipe: object|null, source: 'library'|'model'|'fallback'|'offline', cached: boolean, code?: string, rateKey?: string }>}
 *   rateKey: pass to POST /api/rate (src/ui/rating.js) - only for library and real model figures
 */
// allowModel false: only library / cached figures (the player cannot pay for a new one) -> source 'needs_diamonds' otherwise
export async function generate(kind, prompt, { era, timeoutMs = 60000, endpoint = '/api/generate', signal, validateBuilding, fallbackBuilding, allowModel = true } = {}) {
  const p = typeof prompt === 'string' ? prompt.trim().slice(0, 160) : '';
  if (isBlocked(p)) return { recipe: null, source: 'blocked', cached: false, code: 'blocked' }; // block list: no request at all
  const offline = code => ({
    recipe: kind === 'building' ? (typeof fallbackBuilding === 'function' ? safe(() => fallbackBuilding(p)) : null) : fallbackUnitRecipe(p),
    source: 'offline', cached: false, code,
  });
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onAbort = () => ctrl.abort();
  if (signal) { if (signal.aborted) ctrl.abort(); else signal.addEventListener('abort', onAbort, { once: true }); }
  try {
    const body = { kind, prompt: p };
    if (!allowModel) body.allowModel = false;
    if (era) body.era = String(era).slice(0, 32);
    const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: ctrl.signal });
    let data = null;
    try { data = await res.json(); } catch { data = null; }
    if (!res.ok || !data || typeof data !== 'object') return offline(res.status === 429 ? 'rate_limited' : `http_${res.status}`);
    if (data.source === 'needs_diamonds') return { recipe: null, source: 'needs_diamonds', cached: false, code: 'needs_diamonds' };
    if (data.source === 'daily_limit') return { recipe: null, source: 'daily_limit', cached: false, code: 'daily_limit' };
    const source = data.source === 'model' || data.source === 'library' ? data.source : 'fallback';
    const rateKey = source !== 'fallback' && typeof data.rateKey === 'string' && data.rateKey.length <= 160 ? { rateKey: data.rateKey } : {};
    if (kind === 'building') {
      if (!data.recipe) return offline(data.code || 'no_recipe');
      if (typeof validateBuilding === 'function') {
        const v = safe(() => validateBuilding(data.recipe));
        if (!v || !v.recipe) return offline('invalid_recipe');
        return { recipe: v.recipe, source, cached: !!data.cached, code: data.code, ...rateKey };
      }
      return { recipe: data.recipe, source, cached: !!data.cached, code: data.code, ...rateKey };
    }
    // defence in depth: re-validate whatever the server sent
    const v = validateUnitRecipe(data.recipe, { fallbackName: p });
    if (!v.ok) return { recipe: fallbackUnitRecipe(p), source: 'fallback', cached: false, code: 'invalid_recipe' };
    // curated library figures are shown exactly as built: never passed through the repair (src/game/recipeRepair.js)
    if (source === 'library') v.recipe.fixed = 'lib';
    return { recipe: v.recipe, source, cached: !!data.cached, code: data.code, ...rateKey };
  } catch (e) {
    return offline(e && e.name === 'AbortError' ? 'timeout' : 'network');
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}

function safe(fn) { try { return fn(); } catch { return null; } }
